import React, { FC, useEffect, useState } from 'react';
import { snakeCase } from 'lodash';
import { useObserver } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';

/* Components */
import TemplateSetsTable from './TemplateSetsTable';
import TemplateSetsEmptyView from './TemplateSetsEmptyView';
import LoadMoreDisplayText from '~/src/components/LoadMoreDisplayText';
import TemplatePreviewDialog from '~/src/components/TemplatePreviewDialog';

/* Hooks */
import usePageScroll from '~/src/hooks/usePageScroll';
import useTemplateSetsPagination from '~/src/hooks/useTemplateSetsPagination';
import { useTemplatePreview } from '~/src/hooks/useTemplatePreview';

/* Store */
import { TemplateSetStore } from '~/src/stores/templateSetsStore';
import { getOrgFprintFromStoreNode } from '~/src/stores/utils';
import useMst from '~/src/hooks/useMst';

/* Services */
import templateSetsService, {
  OutdatedTemplate,
} from '~/src/services/api/templateSetsService';

/* Types */
import { SortByOrder } from '~/src/hooks/sortByOrder';

interface TemplateSetTableRowData {
  id: number;
  title: string | null;
  defaultValuesId: number | null;
  subtitle: string | null;
}

interface Actions {
  view?: (templateSet: Instance<typeof TemplateSetStore>) => void;
  remove?: ({
    template,
    templateSetId,
  }: {
    template: TemplateSetTableRowData;
    templateSetId: number;
  }) => void;
  rename?: (templateSet: Instance<typeof TemplateSetStore>) => void;
  delete?: (templateSet: Instance<typeof TemplateSetStore>) => void;
  generate?: (templateSet: Instance<typeof TemplateSetStore>) => void;
}

interface Props {
  onAddTemplatesToSeletionSidebar: (
    templateSet: Instance<typeof TemplateSetStore>,
  ) => void;
  rowActions?: Actions;
  childRowActions?: Actions;
}

interface PendingAdd {
  templateSet: Instance<typeof TemplateSetStore>;
  tableRowData: {
    id: number;
    title: string;
    children: TemplateSetTableRowData[];
  };
  outdated: OutdatedTemplate[];
}

const TemplateSetsTableWrapper: FC<Props> = ({
  onAddTemplatesToSeletionSidebar,
  rowActions,
  childRowActions,
}) => {
  const pageScroll = usePageScroll();

  const { next, loading, done, data, setSearch, setSortBy, empty, setOrder } =
    useTemplateSetsPagination();

  const { refreshTemplateSet } = useMst((store) => ({
    refreshTemplateSet: store.templateSets.refreshById,
  }));

  const loadMore = next;

  const [pendingAdd, setPendingAdd] = useState<PendingAdd | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [upgradeDecisions, setUpgradeDecisions] = useState<Set<number>>(
    new Set(),
  );

  const [isUpgrading, setIsUpgrading] = useState(false);

  const {
    previewTitle,
    previewImages,
    isPreviewLoading,
    isPreviewOpen,
    openPreview,
    closePreview,
  } = useTemplatePreview();

  const handleOnPageScroll = () => {
    if ((pageScroll === 0 || pageScroll >= 85) && !loading) {
      loadMore();
    }
  };

  const handleSearchChange = (searchValue: string) => {
    setSearch(searchValue);
  };

  const handleSortByChange = (property: string, order: number) => {
    if (!property) {
      return;
    }

    const orderLabel = order > 0 ? SortByOrder.desc : SortByOrder.asc;
    let sortBy = snakeCase(property);

    // TODO: abstract this into a model/transformer lookup
    if (property === 'updatedAt') {
      sortBy = 'last_modified';
    }

    setSortBy(sortBy);
    setOrder(orderLabel);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleOnPageScroll, [pageScroll]);

  useEffect(() => {
    if (pendingAdd) {
      setCurrentStepIndex(0);
      setUpgradeDecisions(new Set());
    }
  }, [pendingAdd]);

  const templateSetsTableData = useObserver(() => {
    return (data as Instance<typeof TemplateSetStore>[]).map((set) => ({
      id: set.id,
      title: set.title,
      isSourceDeprecated: set.deprecatedTemplateIds.length > 0,
      children: set.currentTemplatesWithValues().flatMap((twv) => {
        const id = twv?.template?.id;
        if (id == null) return [];
        const templateId = Number(id);
        return [
          {
            id: templateId,
            title:
              (twv.templateDefaultValues
                ? twv.templateDefaultValues.title
                : twv?.template?.title) ?? null,
            defaultValuesId: twv.templateDefaultValues
              ? twv.templateDefaultValues.id
              : null,
            subtitle: twv?.template?.subtitle ?? null,
            isSourceDeprecated: set.deprecatedTemplateIds.includes(templateId),
          },
        ];
      }),
    }));
  });

  const handleAddWithVersionCheck = async (
    templateSet: Instance<typeof TemplateSetStore>,
  ) => {
    const tableRowData = templateSetsTableData.find(
      (d) => d.id === templateSet.id,
    );
    if (!tableRowData) {
      onAddTemplatesToSeletionSidebar(templateSet);
      return;
    }

    try {
      const orgFprint = getOrgFprintFromStoreNode();
      const result = await templateSetsService.checkVersions(
        orgFprint,
        templateSet.id,
      );

      const upgradeable = result.outdated_templates;

      if (upgradeable.length === 0) {
        onAddTemplatesToSeletionSidebar(templateSet);
      } else {
        setPendingAdd({ templateSet, tableRowData, outdated: upgradeable });
      }
    } catch {
      // If version check fails, proceed with add as normal
      onAddTemplatesToSeletionSidebar(templateSet);
    }
  };

  const handleDismissDialog = () => {
    setPendingAdd(null);
  };

  const executeUpgrade = async (decisions: Set<number>) => {
    if (!pendingAdd) return;

    if (decisions.size === 0) {
      onAddTemplatesToSeletionSidebar(pendingAdd.templateSet);
      setPendingAdd(null);
      return;
    }

    setIsUpgrading(true);
    try {
      const orgFprint = getOrgFprintFromStoreNode();
      const updatedChildren = [...pendingAdd.tableRowData.children];

      const upgradeResults = await Promise.all(
        pendingAdd.outdated
          .filter((o) => decisions.has(o.org_template_id))
          .map((outdated) =>
            templateSetsService
              .upgradeTemplate(
                orgFprint,
                pendingAdd.templateSet.id,
                outdated.org_template_id,
              )
              .then((result) => ({ outdated, result })),
          ),
      );

      for (const { outdated, result } of upgradeResults) {
        const idx = updatedChildren.findIndex(
          (c) => c.id === outdated.org_template_id,
        );
        const existing = idx !== -1 ? updatedChildren[idx] : undefined;
        if (existing !== undefined) {
          updatedChildren[idx] = {
            ...existing,
            id: result.new_template.id,
            title: result.new_template.title ?? existing.title,
            subtitle: result.new_template.subtitle ?? existing.subtitle,
          };
        }
      }

      const updatedSet = {
        ...pendingAdd.tableRowData,
        children: updatedChildren,
      };
      onAddTemplatesToSeletionSidebar(
        updatedSet as unknown as Instance<typeof TemplateSetStore>,
      );

      await refreshTemplateSet(pendingAdd.templateSet.id);
    } catch {
      onAddTemplatesToSeletionSidebar(pendingAdd.templateSet);
    } finally {
      setIsUpgrading(false);
      setPendingAdd(null);
    }
  };

  const advanceStep = (decisions: Set<number>) => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < (pendingAdd?.outdated.length ?? 0)) {
      setCurrentStepIndex(nextIndex);
      setUpgradeDecisions(decisions);
    } else {
      executeUpgrade(decisions);
    }
  };

  const handleStepYes = () => {
    if (!pendingAdd) return;
    const current = pendingAdd.outdated[currentStepIndex];
    if (!current) return;
    const newDecisions = new Set(upgradeDecisions);
    newDecisions.add(current.org_template_id);
    advanceStep(newDecisions);
  };

  const handleStepNo = () => {
    advanceStep(upgradeDecisions);
  };

  const currentOutdated = pendingAdd?.outdated[currentStepIndex] ?? null;
  const totalSteps = pendingAdd?.outdated.length ?? 0;

  if (empty && data.length === 0) {
    return <TemplateSetsEmptyView />;
  }

  return (
    <>
      <TemplateSetsTable
        data={templateSetsTableData}
        visibleItems={templateSetsTableData.length}
        isLoading={false}
        empty={!loading && done && templateSetsTableData.length === 0}
        onAddTemplatesToSeletionSidebar={handleAddWithVersionCheck}
        rowActions={rowActions}
        childRowActions={childRowActions}
        onSearchChange={handleSearchChange}
        onSortByChange={handleSortByChange}
      />
      <LoadMoreDisplayText
        loading={loading}
        done={done}
        onLoadMore={loadMore}
      />

      <Dialog
        open={!!pendingAdd && currentOutdated !== null}
        onClose={handleDismissDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Newer Version Available
          {totalSteps > 1 ? ` (${currentStepIndex + 1} of ${totalSteps})` : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {currentOutdated !== null && (
              <>
                <div>
                  This template set contains an older version:
                  <br />
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <strong>
                      <em>
                        {currentOutdated.current_form_number ??
                          `Document ${currentOutdated.current_pdf_doc_id}`}
                        {currentOutdated.current_form_name
                          ? `, ${currentOutdated.current_form_name}`
                          : ''}{' '}
                        (#{currentOutdated.current_global_template_id})
                      </em>
                    </strong>
                    <Button
                      size="small"
                      onClick={() =>
                        openPreview(
                          currentOutdated.current_global_template_id,
                          currentOutdated.current_form_number ??
                            `Document ${currentOutdated.current_pdf_doc_id}`,
                        )
                      }
                    >
                      Preview
                    </Button>
                  </div>
                </div>
                <br />
                <div>
                  A newer version is available:
                  <br />
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <strong>
                      <em>
                        {currentOutdated.new_form_number
                          ? `${currentOutdated.new_form_number}${
                              currentOutdated.new_form_name
                                ? `, ${currentOutdated.new_form_name}`
                                : ''
                            }`
                          : `Document ${currentOutdated.new_pdf_doc_id}`}{' '}
                        (#{currentOutdated.new_global_template_id})
                      </em>
                    </strong>
                    <Button
                      size="small"
                      onClick={() =>
                        openPreview(
                          currentOutdated.new_global_template_id,
                          currentOutdated.new_form_number ??
                            `Document ${currentOutdated.new_pdf_doc_id}`,
                        )
                      }
                    >
                      Preview
                    </Button>
                  </div>
                </div>
                <br />
                Would you like to upgrade to the newer version before adding to
                your selection?
                <br />
                <br />
                <Alert severity="warning" style={{ textAlign: 'left' }}>
                  This action is permanent. Once upgraded, the older version
                  cannot be restored as it is no longer available in the Court
                  Forms library.
                </Alert>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismissDialog} disabled={isUpgrading}>
            Cancel
          </Button>
          <Button onClick={handleStepNo} disabled={isUpgrading}>
            No, keep current version
          </Button>
          <Button
            onClick={handleStepYes}
            variant="contained"
            disabled={isUpgrading}
          >
            {isUpgrading ? 'Upgrading…' : 'Yes, upgrade to newer version'}
          </Button>
        </DialogActions>
      </Dialog>

      <TemplatePreviewDialog
        open={isPreviewOpen}
        title={previewTitle}
        images={previewImages}
        isLoading={isPreviewLoading}
        onClose={closePreview}
      />
    </>
  );
};

export default TemplateSetsTableWrapper;
